import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';


import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Calendar,
  Package,
  DollarSign,
  LogOut,
  Menu,
  X,
  ClipboardList,
  UserCog,
  Settings,
  ChevronDown,
  Activity,
  ChevronRight,
  BarChart3,
  TrendingUp,
  Receipt,
  Calculator,
  FileText,
  Upload,
  History,
  Lock,
  Sun,
  Moon
} from 'lucide-react';
import { Wallet } from 'lucide-react';




const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { isDark, toggleTheme } = useTheme();
  const normalizedRole = String(user?.role || '').trim().toLowerCase();
  const isSystemAdmin = normalizedRole === 'admin' || normalizedRole === 'system_admin' || normalizedRole === 'system-admin' || normalizedRole === 'system administrator';

  const reportsItems = [
    { path: '/reports/sales', icon: BarChart3, label: 'Sales Report' },
    { path: '/reports/inventory', icon: ClipboardList, label: 'Inventory Report' },
    { path: '/reports/financial', icon: DollarSign, label: 'Financial Report' },
    { path: '/reports/clients', icon: Users, label: 'Client Report' },
  ];

  const ReportsSection = ({
    reportsOpenByPath,
    reportsItems,
    onNavigate,
    locationPath,
  }) => {
    const [reportsOpen, setReportsOpen] = useState(reportsOpenByPath);

    useEffect(() => {
      setReportsOpen(reportsOpenByPath);
    }, [reportsOpenByPath]);

    const anyReportActive = reportsItems.some((i) => locationPath.startsWith(i.path));

    return (
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setReportsOpen((v) => !v)}
          className={`
            w-full flex items-center px-3 py-2.5 mb-1 rounded-lg transition-all duration-200 group
            ${anyReportActive || reportsOpen ? 'bg-white/15 text-white shadow-lg' : 'text-blue-200/70 hover:bg-white/8 hover:text-white'}
          `}
        >
          <BarChart3 size={18} className={anyReportActive || reportsOpen ? 'text-white' : 'text-blue-300/60 group-hover:text-white'} />
          <span className="ml-3 text-sm font-medium">Reports</span>
          {reportsOpen ? (
            <ChevronDown size={16} className="ml-auto text-blue-300/60" />
          ) : (
            <ChevronRight size={16} className="ml-auto text-blue-300/60" />
          )}
        </button>

        {reportsOpen && (
          <div className="pl-3">
            {reportsItems.map((item) => {
              const Icon = item.icon;
              const isActive = locationPath === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={`
                    flex items-center px-3 py-2 mb-1 rounded-lg transition-all duration-200 group
                    ${isActive ? 'bg-white/15 text-white shadow-lg' : 'text-blue-200/70 hover:bg-white/8 hover:text-white'}
                  `}
                >
                  <Icon
                    size={16}
                    className={isActive ? 'text-white' : 'text-blue-300/60 group-hover:text-white'}
                  />
                  <span className="ml-3 text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const menuItems = [

    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/pos', icon: ShoppingCart, label: 'Point of Sale' },
    { path: '/clients', icon: Users, label: 'Clients' },
    { path: '/appointments', icon: Calendar, label: 'Appointments' },
    { path: '/inventory', icon: Package, label: 'Inventory' },
    { path: '/staff', icon: Users, label: 'Staff' },
    { path: '/suppliers', icon: Package, label: 'Suppliers' },
    { path: '/finance', icon: DollarSign, label: 'Finance' },
    ...(isSystemAdmin ? [{ path: '/users', icon: UserCog, label: 'Users' }] : []),

    ...(isSystemAdmin ? [{ path: '/settings', icon: Settings, label: 'Settings' }] : []),
    ...(isSystemAdmin ? [{ path: '/audit-log', icon: FileText, label: 'Audit Log' }] : []),
    { path: '/credits', icon: Wallet, label: 'Credit/Debts' },
  ];

  // Add import to menu items for authorized users (admin only)
  if (isSystemAdmin) {
    menuItems.push({ path: '/import', icon: Upload, label: 'Data Import' });
  }

  // Historical Data Entry - admin only
  if (isSystemAdmin) {
    menuItems.push({ path: '/historical-entry', icon: History, label: 'Historical Entry' });
  }

  // Historical Entries Viewer - admin only
  if (isSystemAdmin) {
    menuItems.push({ path: '/historical-entries', icon: FileText, label: 'Historical Log' });
  }

  // Period Management - admin only
  if (isSystemAdmin) {
    menuItems.push({ path: '/periods', icon: Lock, label: 'Periods' });
  }







  const handleLogout = () => {
    logout();
    navigate('/login');
    setUserMenuOpen(false);
  };




  return (
    <div className="flex h-screen bg-[var(--bg-light)] dark:bg-[#151521] overflow-hidden">
      {/* Global background blob secondary */}
      <div className="background-blob-secondary"></div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-30 w-64 flex flex-col bg-gradient-to-b from-[var(--navy-main)] to-[var(--navy-darker)] 
        transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Decorative gradient line at top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-green via-brand-blue to-brand-purple"></div>

        {/* Logo Section - Fixed height */}
        <div className="flex-shrink-0 p-4 border-b border-white/10 relative">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3 group">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 via-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/20 transform -rotate-6 group-hover:rotate-0 transition-all duration-500">
                    <span className="text-white font-bold text-xl rotate-6 group-hover:rotate-0 transition-all duration-500">N</span>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-navy-dark flex items-center justify-center">
                    <span className="text-[8px] font-bold text-green-800">+</span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-bold text-lg leading-tight tracking-wide">NUTRI<span className="text-green-300">AXIS</span></span>
                  <span className="text-blue-300/60 text-[10px] tracking-wider">WELLNESS CLINIC</span>
                </div>
              </div>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-blue-300/60 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Decorative gradient divider */}
        <div className="mx-3 my-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 scrollbar-thin">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const shouldRenderReports = item.path === '/finance';

            return (
              <React.Fragment key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center px-3 py-2.5 mb-1 rounded-lg transition-all duration-200 group relative overflow-hidden
                    ${isActive 
                      ? 'text-white shadow-lg' 
                      : 'text-blue-200/70 hover:bg-white/8 hover:text-white'
                    }
                  `}
                >
                  {/* Active gradient background */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-r from-white/15 to-transparent transition-opacity duration-300
                    ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                  `}></div>
                  
                  {/* Icon with gradient background on active */}
                  <div className={`
                    relative z-10 flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-300
                    ${isActive 
                      ? 'bg-gradient-to-br from-brand-green to-brand-green-600 shadow-lg shadow-brand-green/20' 
                      : 'bg-white/5 group-hover:bg-white/10'
                    }
                  `}>
                    <Icon size={16} className={isActive ? 'text-white' : 'text-blue-300/60 group-hover:text-white'} />
                  </div>
                  <span className="relative z-10 ml-3 text-sm font-medium">{item.label}</span>
                  
                  {/* Active indicator dot */}
                  {isActive && (
                    <div className="relative z-10 ml-auto">
                      <div className="pulse-dot bg-brand-green text-brand-green"></div>
                    </div>
                  )}
                </Link>

                {shouldRenderReports && (
                  <ReportsSection
                    reportsOpenByPath={location.pathname.startsWith('/reports/')}
                    reportsItems={reportsItems}
                    onNavigate={() => setSidebarOpen(false)}
                    locationPath={location.pathname}
                  />
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Decorative gradient divider */}
        <div className="mx-3 my-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

        {/* User Section - Fixed at bottom */}
        <div className="flex-shrink-0 p-3">
          <div className="relative">
            {/* User Info Button */}
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-white/8 transition-all duration-200 group"
            >
              <div className="relative flex-shrink-0">
                <div className="w-9 h-9 bg-gradient-to-br from-brand-green to-brand-green-700 rounded-full flex items-center justify-center shadow-lg shadow-green-500/10">
                  <span className="text-white text-sm font-bold">
                    {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                </div>
                {/* Online status dot */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 border-2 border-navy-dark rounded-full"></div>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-blue-300/60 capitalize truncate">{user?.role}</p>
              </div>
              <ChevronDown size={16} className={`text-blue-300/60 transition-transform duration-300 ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* User Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl shadow-2xl border overflow-hidden animate-fade-in-up">
                <div className="relative">
                  {/* Decorative header gradient */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-green via-brand-blue to-brand-purple"></div>
                  <div className="p-2 pt-3">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    {isSystemAdmin && (
                      <Link
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent rounded-md transition-all"
                      >
                        <Settings size={16} className="mr-2 text-gray-400" />
                        Settings
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-transparent rounded-md transition-all"
                    >
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white/80 dark:bg-[#1e1e2e]/90 backdrop-blur-md shadow-sm flex-shrink-0 relative">
          {/* Animated gradient bottom border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-brand-green/50 via-brand-blue/50 to-brand-purple/50"></div>
          
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Menu size={24} />
              </button>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <img
                    src="/nutriaxis-logo.png"
                    alt="Nutriaxis"
                    className="w-10 h-10 rounded-xl shadow-sm"
                  />
                  <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-brand-green rounded-full border-2 border-white"></div>
                </div>
                <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Welcome back, {user?.full_name?.split(' ')[0] || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-brand-green rounded-full inline-block"></span>
                    {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-all"
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Quick Actions */}
              <Link
                to="/pos"
                className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-brand-green-50 to-brand-green-100 text-brand-green-700 rounded-lg hover:from-brand-green-100 hover:to-brand-green-200 transition-all duration-200 text-sm font-medium shadow-sm"
              >
                <ShoppingCart size={16} />
                <span>New Sale</span>
              </Link>
              
              {/* User Avatar */}
              {isSystemAdmin ? (
                <Link to="/settings" className="flex items-center space-x-2 group">
                  <div className="relative">
                    <div className="w-9 h-9 bg-gradient-to-br from-brand-green to-brand-green-700 rounded-full flex items-center justify-center shadow-lg shadow-green-500/10 group-hover:shadow-green-500/20 transition-shadow">
                      <span className="text-white text-sm font-bold">
                        {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                  </div>
                </Link>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <div className="w-9 h-9 bg-gradient-to-br from-brand-green to-brand-green-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-bold">
                        {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;